import mixpanel from 'mixpanel-browser'
import { defineStore } from 'pinia'
import { reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import UserService from '@/services/api/user.service.js'
import 'firebase/compat/auth'
import { useNotifications } from '@/composables/useNotifications.js'
import { convertToStars } from '@/utils/financials'

export const useAuthStore = defineStore(
    'authUser',
    () => {
        const router = useRouter()
        const state = reactive({
            showKYCModal: false,
            showKYCSuccess: false,
            referralCode: null,
            isLoggingOut: false,
            appInitialized: false,
        })

        const user = reactive({
            email: undefined,
            uid: undefined,
            data: undefined,
        })

        ////// Getters //////

        const userWalletBalanceInStars = computed(() => {
            // Calculate stars from available USD balance to ensure consistency
            // with the USD display (both show available balance, not total balance)
            return convertToStars(user.data?.wallet?.availableBalance || 0)
        })

        const userWalletBalance = computed(() => {
            // Use precise USD balance from backend without rounding
            return user.data?.wallet?.availableBalance || 0
        })

        const userId = computed(() => {
            return user.data?._id || null
        })

        const isAnonymous = computed(() => {
            return user.data?.isAnonymous === true
        })

        ////// Actions //////
        function setUser(authUser) {
            if (!authUser) {
                user.email = null
                user.uid = null
                user.data = null
                state.appInitialized = false
                return
            }

            // Update Firebase user fields
            user.email = authUser.email || null
            user.uid = authUser.uid || null

            // IMPORTANT: Preserve user.data if it already exists
            // This prevents race conditions where onAuthStateChanged clears
            // user.data that was just set by the login flow
            // user.data should only be cleared when authUser is null (logout)
        }

        async function updateUserData(logged) {
            if (!logged) {
                user.data = null
                return null
            }
            try {
                const userData = await UserService.getCurrentUser()
                if (userData.status === 401) {
                    user.data = null
                    return null
                }
                user.data = userData.data

                if (user.data && user.data._id) {
                    state.isLoggingOut = false
                    try {
                        if (
                            typeof window !== 'undefined' &&
                            !window.__mixpanelDisabled &&
                            window.__mixpanelInited &&
                            typeof mixpanel.identify === 'function'
                        ) {
                            mixpanel.identify(
                                user.data._id?.toString?.() || user.data._id
                            )
                            // Sync basic profile data immediately to client SDK
                            mixpanel.people.set({
                                $email: user.data.email,
                                $name: user.data.email,
                                'User Type': user.data.isAnonymous
                                    ? 'Anonymous'
                                    : user.data.typeofuser || 'User',
                                Registered: !user.data.isAnonymous,
                            })
                        }
                    } catch (e) {
                        console.warn('Mixpanel identify failed:', e)
                    }
                    setTimeout(() => {
                        syncNotificationToken()
                    }, 2000)

                    // Apply UI language from backend if available
                    if (user.data.uiLanguage) {
                        const savedLocalLanguage =
                            localStorage.getItem('preferredLanguage')
                        // Only apply backend language if different from current
                        if (savedLocalLanguage !== user.data.uiLanguage) {
                            console.log(
                                'Applying UI language from backend:',
                                user.data.uiLanguage
                            )
                            localStorage.setItem(
                                'preferredLanguage',
                                user.data.uiLanguage
                            )

                            // Load and apply the language
                            import('@/i18n.js')
                                .then(({ loadLocaleResources }) => {
                                    return loadLocaleResources(
                                        user.data.uiLanguage
                                    )
                                })
                                .catch((err) => {
                                    console.error(
                                        'Failed to load UI language from backend:',
                                        err
                                    )
                                })
                        }
                    }
                }

                return user.data
            } catch (error) {
                console.error('Error fetching user data:', error)
                user.data = null
                throw error
            }
        }

        function updateListingStatus(status, userSelectedListingStatus) {
            if (!user.data) return
            if (typeof status === 'string') {
                user.data.listingStatus = status
            }
            if (typeof userSelectedListingStatus === 'boolean') {
                user.data.userSelectedListingStatus = userSelectedListingStatus
            }
        }

        async function refreshUserBalance() {
            try {
                const userResponse = await UserService.getCurrentUser()

                if (userResponse.status >= 200 && userResponse.status < 300) {
                    user.data = userResponse.data
                } else {
                    console.error(
                        'Error refreshing user balance:',
                        userResponse
                    )
                    user.data = null // Clear data on error
                }
            } catch (error) {
                console.error('Error refreshing user balance:', error)
                user.data = null // Clear data on error
            }
        }

        // Sync notification token with backend if available
        async function syncNotificationToken() {
            console.log('Syncing notification token...')

            // Skip sync if it was done recently (avoid redundant calls)
            const lastSync = localStorage.getItem('lastTokenSync')
            const now = Date.now()
            if (lastSync && now - parseInt(lastSync) < 30000) {
                // 30 seconds
                console.log('Token sync skipped (done recently)')
                return
            }

            try {
                const { checkNotificationStatus } = useNotifications()
                await checkNotificationStatus()
                localStorage.setItem('lastTokenSync', now.toString())
            } catch (error) {
                console.error('Error syncing notification token:', error)
            }
        }

        // Toggle the users translation setting in this user based on the current setting
        async function toggleTranslation(enabled) {
            // Ensure user data exists before proceeding
            if (!user.data) {
                console.warn('Cannot toggle translation: user data not loaded.')
                return false
            }
            // If we already have the setting, return
            if (user.data.translateMessages === enabled) return true

            // If we're turning translation on, ensure we have a preferred language set
            const updates = {
                translateMessages: enabled,
            }

            // If turning on translation and no preferred language is set, use browser language
            if (enabled && !user.data.preferredLanguage) {
                updates.preferredLanguage = navigator.language // Get base language e.g., 'en' from 'en-US'
            }

            try {
                const updatedUser = await UserService.updateUser(updates)
                if (updatedUser.status == 200) {
                    user.data = updatedUser.data
                    return true
                } else {
                    console.error(
                        'Failed to update translation setting:',
                        updatedUser
                    )
                    return false
                }
            } catch (error) {
                console.error('Error updating translation setting:', error)
                return false
            }
        }

        // Atomic action to enable translation AND set language simultaneously
        async function enableTranslationWithLanguage(language) {
             if (!user.data) {
                console.warn('Cannot enable translation: user data not loaded.')
                return false
            }
            
            const updates = {
                translateMessages: true,
                preferredLanguage: language
            }

            try {
                const updatedUser = await UserService.updateUser(updates)
                if (updatedUser.status == 200) {
                    user.data = updatedUser.data
                    return true
                } else {
                    console.error(
                        'Failed to enable translation with language:',
                        updatedUser
                    )
                    return false
                }
            } catch (error) {
                console.error('Error enabling translation with language:', error)
                return false
            }
        }

        // New function to update preferred language
        async function updatePreferredLanguage(language) {
            if (!user.data) {
                console.warn(
                    'Cannot update preferred language: user data not loaded.'
                )
                return false
            }
            try {
                const updatedUser = await UserService.updateUser({
                    preferredLanguage: language,
                })

                if (updatedUser.status == 200) {
                    user.data = updatedUser.data
                    return true
                } else {
                    console.error(
                        'Failed to update preferred language:',
                        updatedUser
                    )
                    return false
                }
            } catch (error) {
                console.error('Error updating preferred language:', error)
                return false
            }
        }

        async function logout() {
            const { logout } = await import('@/utils/auth.js')
            await logout()
            router.push({ name: 'auth' })
        }

        //Update KYC status
        function updateKYCStatus(status) {
            if (!user.data?.kyc) return
            user.data.kyc.status = status
            state.showKYCModal = false
        }

        function logoutClean() {
            state.isLoggingOut = true
            state.appInitialized = false
            user.data = null
            user.email = null
            user.uid = null

            // Clean state
            state.showKYCModal = false
            state.showKYCSuccess = false

            // Clear session storage items related to onboarding
            sessionStorage.removeItem('onboardingUserType')

            // Track Logout and Reset Mixpanel to ensure separation of accounts
            try {
                if (
                    typeof window !== 'undefined' &&
                    !window.__mixpanelDisabled &&
                    window.__mixpanelInited
                ) {
                    if (typeof mixpanel.track === 'function') {
                        mixpanel.track('Logout')
                    }
                    if (typeof mixpanel.reset === 'function') {
                        mixpanel.reset()
                    }
                }
            } catch (e) {
                console.warn('Mixpanel logout/reset failed:', e)
            }
        }

        function setAppInitialized(initialized) {
            state.appInitialized = !!initialized
        }
        return {
            state,
            user,
            userWalletBalanceInStars,
            userWalletBalance,
            userId,
            isAnonymous,
            setUser,
            updateUserData,
            refreshUserBalance,
            toggleTranslation,
            updatePreferredLanguage,
            enableTranslationWithLanguage,
            updateKYCStatus,
            syncNotificationToken,
            updateListingStatus,
            logoutClean,
            logout,
            setAppInitialized,
        }
    },
    {
        persist: {
            storage: localStorage,
            // Persist only safe auth fields; never persist user.data
            paths: ['user.uid', 'user.email'],
        },
    }
)
